/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { EditCustomerInformationProps, FieldsProps } from '@/account/types';
import {
  Button,
  Card,
  Header,
  InLineAlert,
  InputPassword,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Form } from '@/account/components';
import '@/account/components/EditCustomerInformation/EditCustomerInformation.css';

export const EditCustomerInformation: FunctionComponent<
  EditCustomerInformationProps
> = ({
  inLineAlertProps,
  errorPasswordEmpty,
  passwordValue,
  showPasswordOnEmailChange,
  submitLoading,
  formFieldsList,
  handleHideEditForm,
  handleUpdateCustomerInformation,
  handleInputChange,
  handleSetPassword,
  handleOnBlurPassword,
}) => {
  const translations = useText({
    buttonSecondary:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.buttonSecondary',
    buttonPrimary:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.buttonPrimary',
    placeholder:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.passwordField.placeholder',
    floatingLabel:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.passwordField.floatingLabel',
    containerTitle:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.containerTitle',
    requiredFieldError: 'Account.FormText.requiredFieldError',
  });

  return (
    <Card variant="secondary" className={'account-edit-customer-information'}>
      <Header
        title={translations.containerTitle}
        divider={false}
        className={'account-edit-customer-information__title'}
      />
      {inLineAlertProps.text ? (
        <InLineAlert
          className="account-edit-customer-information__notification"
          type={inLineAlertProps.type}
          variant="secondary"
          heading={inLineAlertProps.text}
          icon={inLineAlertProps.icon}
          data-testid="editCustomerInLineAlert"
        />
      ) : null}
      <Form
        loading={submitLoading}
        fieldsConfig={(formFieldsList as FieldsProps[]) || []}
        name="editCustomerInformation"
        className="account-edit-customer-information-form"
        onSubmit={handleUpdateCustomerInformation}
        setInputChange={handleInputChange}
      >
        {showPasswordOnEmailChange ? (
          <div className="account-edit-customer-information__password">
            <InputPassword
              autoComplete="password"
              name="password"
              placeholder={translations.placeholder}
              floatingLabel={translations.floatingLabel}
              errorMessage={
                errorPasswordEmpty ? translations.requiredFieldError : undefined
              }
              defaultValue={passwordValue}
              onValue={handleSetPassword}
              onBlur={handleOnBlurPassword}
            />
          </div>
        ) : null}
        <div className="account-edit-customer-information__actions">
          <Button
            disabled={submitLoading}
            // @ts-ignore
            type="button"
            variant="secondary"
            onClick={() => handleHideEditForm()}
          >
            {translations.buttonSecondary}
          </Button>
          <Button disabled={submitLoading} type="submit" variant="primary">
            {translations.buttonPrimary}
          </Button>
        </div>
      </Form>
    </Card>
  );
};
